<template>
  <div
    class="search-dropdown"
    :class="{'search-dropdown_disabled': disabled}"
    :style="{ width: width }"
    ref="dropdown">
    <div class="search-dropdown__input">
      <el-input
        v-model="value"
        :placeholder="placeholder"
        @input="debouncedSearch"
        @focus="onFocus"
      />
      <div
        class="search-dropdown__input-icon"
        :class="{'search-dropdown__input-icon_rotate': isOpen}"
      >
        <BasePreloader :size="'m'" :color="'purple'" v-if="loading"></BasePreloader>
        <img src="/img/renova-dashboard/icons/chevron-down.svg" alt="" v-if="!loading" />
      </div>
    </div>
    <div class="search-dropdown__options" v-if="isOpen && options && options.length" @scroll="onScroll">
      <div
        class="search-dropdown__option"
        v-for="option in options"
        :class="{
          'search-dropdown__option_selected': selectedOption === option.id,
          'search-dropdown__option_disabled': option.disabled
        }"
        :key="option.id"
        @click="selectOption(option)"
      >
        <p>{{ option.name || option.title }}</p>
        <span class="color--dark-purple">&nbsp;{{option.hint}}</span>
      </div>
    </div>
    <div class="search-dropdown__options" v-if="isOpen && options && !options.length && !loading">
      <p class="color--dark-purple" style="text-align: left; line-height: 22px; padding: 8px 16px;">
        {{ noDataText || $t('tasks.NoMatchingOptionsFound') }}
      </p>
    </div>
  </div>
</template>

<script>
import BasePreloader from '@/views/RenovaDashboard/components/ui-kit/Buttons/BasePreloader';

export default {
  name: 'SearchDropdown',
  components: { BasePreloader },
  emits: ['search', 'select', 'load-more'],
  props: {
    selectedOption: {
      type: [String, Number],
      required: false,
    },
    loading: {
      type: Boolean,
      required: false,
    },
    disabled: {
      type: Boolean,
      required: false,
    },
    placeholder: {
      type: String,
      required: true,
    },
    options: {
      required: true,
    },
    noDataText: {
      type: String,
      default: '',
    },
    width: {
      type: String,
      default: '100%',
    },
  },
  data() {
    return {
      value: '',
      isOpen: false,
    };
  },
  watch: {
    selectedOption: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          const selectedOptionValue = this.options?.find((val) => val.id === newVal);
          if (selectedOptionValue) {
            this.value = selectedOptionValue.name || selectedOptionValue.title;
          }
        } else {
          this.value = '';
        }
      },
    },
    options: {
      handler(newOptions) {
        if (this.selectedOption && !this.isOpen) {
          const selectedOptionValue = newOptions?.find((val) => val.id === this.selectedOption);
          if (selectedOptionValue) {
            this.value = selectedOptionValue.name || selectedOptionValue.title;
          }
        }
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside);
  },
  methods: {
    debouncedSearch(event) {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }
      this.debounceTimeout = setTimeout(() => {
        this.$emit('search', event);
      }, 300);
    },
    onFocus() {
      this.isOpen = true;
      this.value = '';
      this.$emit('search', '');
    },
    selectOption(option) {
      if (!this.disabled && !option.disabled) {
        this.value = option.name || option.title;
        this.$emit('select', option);
        this.isOpen = false;
      }
    },
    onClickOutside(event) {
      if (this.isOpen && this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false;
        if (this.selectedOption) {
          const selectedOptionValue = this.options?.find((val) => val.id === this.selectedOption);
          if (selectedOptionValue) {
            this.value = selectedOptionValue.name || selectedOptionValue.title;
          }
        } else {
          this.value = '';
        }
      }
    },
    onScroll(event) {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        this.$emit('load-more');
      }
    },
    destroyed() {
      document.removeEventListener('click', this.onClickOutside);
    },
  },
};
</script>

<style lang="scss">
.search-dropdown {
  position: relative;
  width: 100%;
  &_disabled{
    * {
      pointer-events: none;
    }
    opacity: 0.8;
    input{
      background: #F8F8F8 !important;
    }
  }
}

.search-dropdown__input {
  position: relative;
  width: 100%;
}

.search-dropdown__input-icon {
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%) rotate(90deg);
  transition: .3s;
  display: flex;
  &_rotate{
    transform: translateY(-50%) rotate(270deg);
  }
}

.search-dropdown__options {
  position: absolute;
  top: calc(100% + 4px);
  width: 100%;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 4px;
  border: 1px solid var(--solid-lavender-grey);
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  z-index: 10;
  max-height: 320px;
  overflow: auto;
}

.search-dropdown__option {
  position: relative;
  border-radius: 6px;
  min-height: 40px;
  height: auto;
  padding: 8px 16px;
  display: flex;
  align-items: center;
  line-height: 22px;
  &:hover {
    background-color: var(--solid-grey-light) !important;
  }
  &_disabled{
    &:after {
      display: none;
    }
  }
  &_selected {
    color: var(--solid-purple) !important;

    &:after {
      position: absolute;
      right: 20px;
      font-family: element-icons;
      content: "\e6da";
      font-size: 12px;
      font-weight: 700;
      -webkit-font-smoothing: antialiased;
    }
  }

  &_disabled {
    opacity: 0.6;
    cursor: not-allowed;
    color: var(--solid-grey-dark) !important;

    &:hover {
      background-color: transparent !important;
    }
  }
}
.search-dropdown__input{
  input{
    padding-right: 50px !important;
  }
}
</style>
